import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getProvinceReports = createAsyncThunk(
  "reports/getProvinceReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().reports;
      if (state.count.getProvinceReports <= 1) {
        const res = await axios.get("/reports/province-projects", {
          params: reqData,
        });
        return res.data;
      }
      return state.province;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getNarativeReports = createAsyncThunk(
  "reports/getNarativeReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().reports;
      if (state.count.getNarativeReports <= 1) {
        const res = await axios.get("/reports/naratives", {
          params: reqData,
        });
        return res.data;
      }
      return { data: state.naratives, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getRealProjectReports = createAsyncThunk(
  "reports/getRealProjectReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().reports;
      if (state.count.getRealProjectReports <= 1) {
        const res = await axios.get("/reports/projects", {
          params: reqData,
        });
        return res.data;
      }
      return { data: state.projects, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getCommunityProjectReports = createAsyncThunk(
  "reports/getCommunityProjectReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().reports;
      if (state.count.getCommunityProjectReports <= 1) {
        const res = await axios.get("/reports/community-projects", {
          params: reqData,
        });
        return res.data;
      }
      return { data: state.projects, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const reportsSlice = createSlice({
  name: "reports",
  initialState: {
    total: 0,
    transactions: [],
    accounts: [],

    naratives: [],
    organizations: [],
    communities: [],
    projects: [],
    data: [],

    organization: {},
    community: {},
    province: {},
    project: {},

    transaction: {},
    account: {},

    error: null,
    load: {
      getProvinceReports: false,
      getNarativeReports: false,
      getRealProjectReports: false,
      getCommunityProjectReports: false,
    },
    count: {
      getProvinceReports: 0,
      getNarativeReports: 0,
      getRealProjectReports: 0,
      getCommunityProjectReports: 0,
    },
  },
  reducers: {
    setProjectsReports: (state, action) => {
      state.data = action.payload;
    },
    unsetReports: (state) => {
      state.total = 0;
      state.transactions = [];
      state.accounts = [];

      state.naratives = [];
      state.organizations = [];
      state.communities = [];
      state.projects = [];
      state.data = [];

      state.organization = {};
      state.community = {};
      state.province = {};
      state.project = {};

      state.transaction = {};
      state.account = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Province Reports
      .addCase(getProvinceReports.pending, (state) => {
        state.load.getProvinceReports = loadStatus.loading;
        state.error = null;
        state.count.getProvinceReports = ++state.count.getProvinceReports;
      })
      .addCase(getProvinceReports.fulfilled, (state, action) => {
        state.load.getProvinceReports = loadStatus.success;
        state.province = action.payload;
        state.count.getProvinceReports = 0;
      })
      .addCase(getProvinceReports.rejected, (state, action) => {
        state.load.getProvinceReports = loadStatus.error;
        state.error = action.payload;
        state.count.getProvinceReports = 0;
      })
      //Get all Real Projects Reports
      .addCase(getRealProjectReports.pending, (state) => {
        state.load.getRealProjectReports = loadStatus.loading;
        state.error = null;
        state.count.getRealProjectReports = ++state.count.getRealProjectReports;
      })
      .addCase(getRealProjectReports.fulfilled, (state, action) => {
        state.load.getRealProjectReports = loadStatus.success;
        state.projects = action.payload.data;
        state.total = action.payload.count;
        state.count.getRealProjectReports = 0;
      })
      .addCase(getRealProjectReports.rejected, (state, action) => {
        state.load.getRealProjectReports = loadStatus.error;
        state.error = action.payload;
        state.count.getRealProjectReports = 0;
      })
      //Get all Community Projects Reports
      .addCase(getCommunityProjectReports.pending, (state) => {
        state.load.getCommunityProjectReports = loadStatus.loading;
        state.error = null;
        state.count.getCommunityProjectReports = ++state.count
          .getCommunityProjectReports;
      })
      .addCase(getCommunityProjectReports.fulfilled, (state, action) => {
        state.load.getCommunityProjectReports = loadStatus.success;
        state.projects = action.payload.data;
        state.total = action.payload.count;
        state.count.getCommunityProjectReports = 0;
      })
      .addCase(getCommunityProjectReports.rejected, (state, action) => {
        state.load.getCommunityProjectReports = loadStatus.error;
        state.error = action.payload;
        state.count.getCommunityProjectReports = 0;
      })
      //Get all Province Reports
      .addCase(getNarativeReports.pending, (state) => {
        state.load.getNarativeReports = loadStatus.loading;
        state.error = null;
        state.count.getNarativeReports = ++state.count.getNarativeReports;
      })
      .addCase(getNarativeReports.fulfilled, (state, action) => {
        state.load.getNarativeReports = loadStatus.success;
        state.naratives = action.payload.data;
        state.total = action.payload.count;
        state.count.getNarativeReports = 0;
      })
      .addCase(getNarativeReports.rejected, (state, action) => {
        state.load.getNarativeReports = loadStatus.error;
        state.error = action.payload;
        state.count.getNarativeReports = 0;
      });
  },
});

export const { setProjectsReports, unsetReports } = reportsSlice.actions;
export default reportsSlice.reducer;
